import React, {useState} from 'react';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {Experiments, SurveyListItem, CreativeBox, BordomatikBox} from 'client/common/types';
import {Spin, RadioButton} from 'client/common/components/lego';
import ClientSelect from 'client/common/components/client-select';
import Modal from 'client/common/components/modal';
import {StateFilterValue} from 'client/common/store/reducers/surveys/types';
import {useUser} from 'client/common/contexts/user';
import {useCurrentOrg} from 'client/common/hooks/orgs';

import Onboarding from 'client/creatives/components/onboarding';
import CreativeLoader from 'client/creatives/components/creative-loader';
import {useCreativeSurveys} from 'client/creatives/hooks/creatives';
import {CreativesBoxType} from 'client/creatives/types';
import {getIsOnboardingShowed, setIsOboardingShowed} from 'client/creatives/utils/onboarding';

import {LoadMoreBtn} from './load-more-btn';
import {ListItem} from './list-item';
import {CreativeInfo} from './creative-info';

import './creative-list.css';

const i18n = I18N.creatives.creativeList;
const b = b_.with('creative-list');

const STATE_FILTER_OPTIONS = [
    {
        value: StateFilterValue.All,
        children: i18n.stateFilter.all
    },
    {
        value: StateFilterValue.Completed,
        children: i18n.stateFilter.rated
    }
];

export interface CreativeListProps {
    boxType: CreativesBoxType;
    experiments: Experiments;
}

export default function CreativeList({boxType}: CreativeListProps) {
    const [stateFilter, setStateFilter] = useState(StateFilterValue.All);
    const [clientId, setClientId] = useState<string>();
    const orgId = useCurrentOrg();

    const {surveys, isFetching, isFetchingMore, canFetchMore, fetchMore} = useCreativeSurveys(
        boxType,
        stateFilter,
        clientId,
        orgId
    );
    const ownSurveys = surveys.filter(({isDemo}) => !isDemo);
    const user = useUser();

    const isOnboardingShowed = getIsOnboardingShowed(boxType);
    const isOnboardingVisible = !isOnboardingShowed && !isFetching && ownSurveys.length === 0;

    return (
        <div className={b()}>
            {boxType === 'creative' && <CreativeInfo boxType={boxType} userInfo={user?.info} />}
            <div className={b('header')}>
                <div className={b('header-filters')}>
                    <h1 className={b('title')}>{i18n.title[boxType]}</h1>
                    <RadioButton
                        onChange={e => setStateFilter(e.target.value as StateFilterValue)}
                        options={STATE_FILTER_OPTIONS}
                        size='m'
                        value={stateFilter}
                        view='default'
                    />
                    {user?.info?.authInfo?.isAgency && (
                        <ClientSelect
                            className={b('client-select')}
                            onChange={id => setClientId(id)}
                            selectedId={clientId}
                            showRemainPackages
                        />
                    )}
                </div>
                <CreativeLoader boxType={boxType} clientId={clientId} />
            </div>
            {isFetching && !isFetchingMore ? (
                <div className={b('loader')}>
                    <Spin className={b('spin')} size='s' view='default' progress />
                </div>
            ) : (
                <div className={b('list')}>
                    {surveys.map(
                        ({slug, state, box, name, isDemo, audience, resources}: SurveyListItem) => (
                            <ListItem
                                key={slug}
                                slug={slug}
                                state={state}
                                box={box as CreativeBox | BordomatikBox}
                                name={name}
                                audience={audience}
                                resources={resources}
                                isDemo={isDemo}
                            />
                        )
                    )}
                </div>
            )}

            {canFetchMore && (
                <LoadMoreBtn
                    fetchMore={fetchMore}
                    canFetchMore={canFetchMore}
                    isFetchingMore={isFetchingMore}
                />
            )}
            <Onboarding
                visible={isOnboardingVisible}
                boxName={boxType}
                onShowWelcome={() => setIsOboardingShowed(boxType)}
            />
            <Modal />
        </div>
    );
}
